// =============================================================================
// features/sales/campaigns/operate/campaignStatus.ts — Texto explicativo
// quando a campanha NÃO está em condição de ser operada agora (doc 26 §6.8 +
// briefing do slot: "Toda restrição/bloqueio (ex.: campanha pausada) precisa
// ser comunicada por TEXTO, não só por cor/ícone").
//
// A API (`GET /queue/claim-next`) não impede reservar itens de uma campanha
// pausada/rascunho/concluída (ver apps/api/.../queue/repository.ts
// `selectNextAvailableMemberForUpdate` — não filtra por `sales_campaigns.status`).
// Esta tela é quem decide não nem tentar: melhor a barreira aparecer ANTES
// de reservar um cliente do que o vendedor começar a atender e descobrir no
// meio que a campanha foi pausada por outro gestor.
// =============================================================================

import type { CampaignStatus } from '@elemento/shared-schemas';

export interface CampaignOperableCheck {
  operable: boolean;
  title: string;
  description: string;
}

const NOT_OPERABLE: Record<Exclude<CampaignStatus, 'active'>, CampaignOperableCheck> = {
  draft: {
    operable: false,
    title: 'Campanha ainda em rascunho',
    description:
      'Esta campanha não foi ativada. Não há fila para operar até que um gestor a ative.',
  },
  ready: {
    operable: false,
    title: 'Campanha pronta, mas não ativada',
    description:
      'O público já foi definido, mas a campanha ainda não está ativa. Peça a um gestor para ativá-la antes de operar.',
  },
  paused: {
    operable: false,
    title: 'Campanha pausada',
    description:
      'Um gestor pausou esta campanha — a operação fica temporariamente indisponível até ela ser retomada.',
  },
  completed: {
    operable: false,
    title: 'Campanha concluída',
    description: 'Esta campanha já foi encerrada. Não há mais clientes para trabalhar aqui.',
  },
  cancelled: {
    operable: false,
    title: 'Campanha cancelada',
    description: 'Esta campanha foi cancelada. Não há mais clientes para trabalhar aqui.',
  },
};

/** `operable: true` só para `status === 'active'` — todo o resto tem um texto explicando exatamente por quê. */
export function checkCampaignOperable(status: CampaignStatus): CampaignOperableCheck {
  if (status === 'active') {
    return { operable: true, title: '', description: '' };
  }
  return NOT_OPERABLE[status];
}
